/**
 * MDI path -> Lucide component translation table.
 *
 * The app historically passed resolved `@mdi/js` path strings around (`:path="mdiRocket"`,
 * `:icon="mdiCog"`). Rather than editing every call site, `components/Icon.vue` looks the
 * incoming path string up here and renders the equivalent Lucide component instead.
 *
 * Fallback behaviour: `lucideForMdiPath()` returns `null` when there is no good Lucide
 * equivalent, and Icon.vue then draws the original MDI path as a plain <svg><path>. So an
 * unmapped icon is always safe — it simply keeps its filled MDI look instead of the stroked
 * Lucide look. Never map an icon to a "close enough but wrong" Lucide glyph just to get
 * coverage; leaving it to the MDI fallback is the better outcome.
 *
 * Safe, but silent: eleven outline icons once sat filled beside stroked ones unnoticed. So in
 * development Icon.vue warns once per unmapped path, skipping `intentionallyUnmapped` below.
 *
 * Notes on the mapping:
 * - Keys are path DATA strings, not names. Every MDI name used in this codebase was verified
 *   to be a real `@mdi/js` export with unique path data (no duplicate keys). Coverage is
 *   tracked in docs/icons.md.
 * - MDI ships filled + outline variants of most icons (`mdiAccount` / `mdiAccountOutline`).
 *   Lucide has a single stroked style, so both variants map to the same component. Expected.
 * - Lucide renamed a lot of icons in v1 (`AlertCircle`->`CircleAlert`, `LineChart`->`ChartLine`,
 *   `Home`->`House`, `MoreVertical`->`EllipsisVertical`, ...). The modern names are used here.
 * - Brand marks are intentionally NOT mapped: `mdiDiscord`, `mdiGoogle`, `mdiGithub`.
 *   Lucide has dropped/deprecated brand logos, so these keep rendering their MDI glyph.
 */

import {
  mdiAbTesting,
  mdiAccount,
  mdiAccountCircle,
  mdiAccountDetails,
  mdiAccountGroup,
  mdiAccountGroupOutline,
  mdiAccountMultiple,
  mdiAccountMultiplePlusOutline,
  mdiAccountOffOutline,
  mdiAccountOutline,
  mdiAccountPlusOutline,
  mdiAlert,
  mdiAlertCircle,
  mdiAlertCircleOutline,
  mdiAlertDecagram,
  mdiAlertOctagonOutline,
  mdiAlertOutline,
  mdiApi,
  mdiArrowLeft,
  mdiArrowRight,
  mdiFormatListBulleted,
  mdiAsterisk,
  mdiAutorenew,
  mdiBackburger,
  mdiBallot,
  mdiBallotOutline,
  mdiBasketPlusOutline,
  mdiBellOutline,
  mdiBellRingOutline,
  mdiBook,
  mdiBookOpenPageVariant,
  mdiBugOutline,
  mdiCalendarBlankOutline,
  mdiCalendarClock,
  mdiCallSplit,
  mdiCancel,
  mdiCardSearchOutline,
  mdiCart,
  mdiCartOutline,
  mdiCartPlus,
  mdiCashMinus,
  mdiCashMultiple,
  mdiCashPlus,
  mdiCellphoneKey,
  mdiChartBar,
  mdiChartBoxOutline,
  mdiChartLine,
  mdiChartPie,
  mdiChartTimelineVariant,
  mdiChat,
  mdiChatProcessingOutline,
  mdiCheck,
  mdiCheckCircle,
  mdiCheckCircleOutline,
  mdiCheckDecagram,
  mdiCheckDecagramOutline,
  mdiCheckboxBlankOutline,
  mdiCheckboxMarked,
  mdiChevronDown,
  mdiChevronLeft,
  mdiChevronRight,
  mdiChevronUp,
  mdiClipboardCheckOutline,
  mdiClock,
  mdiClockOutline,
  mdiClose,
  mdiCloseCircle,
  mdiCloseCircleOutline,
  mdiCloudOffOutline,
  mdiCodeBraces,
  mdiCog,
  mdiCogOutline,
  mdiCompassOutline,
  mdiContentCopy,
  mdiContentDuplicate,
  mdiContentSaveOutline,
  mdiCreditCardOutline,
  mdiDelete,
  mdiDiscord,
  mdiDotsVertical,
  mdiDownload,
  mdiDownloadOutline,
  mdiEarth,
  mdiEmail,
  mdiEmailAlert,
  mdiEmailAlertOutline,
  mdiEmailCheck,
  mdiEmailFastOutline,
  mdiEmailOutline,
  mdiEye,
  mdiEyeOff,
  mdiEyeOffOutline,
  mdiEyeOutline,
  mdiFeather,
  mdiFileDocumentOutline,
  mdiFileDownloadOutline,
  mdiFileExportOutline,
  mdiFilterRemoveOutline,
  mdiFinance,
  mdiFlashOutline,
  mdiFlaskOutline,
  mdiFormTextboxPassword,
  mdiFormatListBulletedTriangle,
  mdiFormatListChecks,
  mdiFormatListNumbered,
  mdiForwardburger,
  mdiFrequentlyAskedQuestions,
  mdiGift,
  mdiGiftOutline,
  mdiGithub,
  mdiGoogle,
  mdiHandshakeOutline,
  mdiHelpCircleOutline,
  mdiHomeOutline,
  mdiInboxArrowDown,
  mdiInformation,
  mdiInformationOutline,
  mdiKeyOutline,
  mdiLifebuoy,
  mdiLightbulbOnOutline,
  mdiLightningBolt,
  mdiLinkVariant,
  mdiLinkVariantOff,
  mdiLock,
  mdiLockReset,
  mdiLoginVariant,
  mdiLogout,
  mdiMagnify,
  mdiMagnifyClose,
  mdiMail,
  mdiMenu,
  mdiMessageText,
  mdiMonitorCellphone,
  mdiNewspaperVariantOutline,
  mdiOpenInNew,
  mdiPackageVariant,
  mdiPackageVariantClosed,
  mdiPageFirst,
  mdiPageLast,
  mdiPause,
  mdiPauseCircle,
  mdiPauseCircleOutline,
  mdiPencil,
  mdiPercentOutline,
  mdiPlay,
  mdiPlayBoxOutline,
  mdiPlayCircle,
  mdiPlayCircleOutline,
  mdiPlus,
  mdiPlusMinusVariant,
  mdiPostOutline,
  mdiQrcode,
  mdiReceipt,
  mdiReceiptOutline,
  mdiRefresh,
  mdiReload,
  mdiRobotOutline,
  mdiRocket,
  mdiRocketLaunch,
  mdiRocketLaunchOutline,
  mdiRouterNetwork,
  mdiSchool,
  mdiSelectColor,
  mdiSend,
  mdiShieldAccountOutline,
  mdiShieldAlertOutline,
  mdiShieldCheck,
  mdiShieldCheckOutline,
  mdiShieldLock,
  mdiShieldLockOutline,
  mdiShieldOff,
  mdiShieldOutline,
  mdiShuffleVariant,
  mdiSort,
  mdiSortAscending,
  mdiSortDescending,
  mdiSortVariant,
  mdiSourceBranch,
  mdiSpeedometer,
  mdiSpeedometerSlow,
  mdiStar,
  mdiStarOutline,
  mdiTableBorder,
  mdiTableOff,
  mdiTagMultiple,
  mdiTagOutline,
  mdiTextBoxOutline,
  mdiTextBoxSearchOutline,
  mdiThemeLightDark,
  mdiTimelineTextOutline,
  mdiTranslate,
  mdiTrashCan,
  mdiTrendingDown,
  mdiTrendingNeutral,
  mdiTrendingUp,
  mdiTrophyVariantOutline,
  mdiTuneVariant,
  mdiTuneVertical,
  mdiUpload,
  mdiViewDashboard,
  mdiViewGridOutline,
  mdiVote,
  mdiWalletOutline,
  mdiWeatherNight,
  mdiWeb,
  mdiWhiteBalanceSunny,
  mdiWrench
} from '@mdi/js'

import {
  ArrowDownWideNarrow,
  ArrowLeft,
  ArrowRight,
  ArrowUpDown,
  ArrowUpNarrowWide,
  Asterisk,
  BadgeAlert,
  BadgeCheck,
  Ban,
  Banknote,
  BanknoteArrowDown,
  Bell,
  BellRing,
  Book,
  Braces,
  BookOpen,
  Bot,
  Bug,
  Calendar,
  CalendarClock,
  ChartColumn,
  ChartLine,
  ChartPie,
  ChartSpline,
  Check,
  ChevronDown,
  ChevronFirst,
  ChevronLast,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  CircleAlert,
  CircleCheck,
  CirclePause,
  CirclePlay,
  CircleQuestionMark,
  CircleUser,
  CircleX,
  ClipboardCheck,
  ClipboardList,
  Clock,
  Compass,
  CloudOff,
  ContactRound,
  Copy,
  CopyPlus,
  CreditCard,
  Diff,
  Download,
  EllipsisVertical,
  ExternalLink,
  Eye,
  EyeOff,
  Feather,
  FileDown,
  FileOutput,
  FileSearch,
  FileText,
  FlaskConical,
  FunnelX,
  Gauge,
  Gift,
  GitBranch,
  Globe,
  GraduationCap,
  Grid2x2X,
  Handshake,
  History,
  House,
  Inbox,
  Info,
  KeyRound,
  Languages,
  LayoutDashboard,
  LayoutGrid,
  LifeBuoy,
  Lightbulb,
  Link,
  List,
  ListChecks,
  ListFilter,
  ListOrdered,
  Lock,
  LogIn,
  LogOut,
  Mail,
  MailCheck,
  MailWarning,
  Menu,
  MessageCircle,
  MessageCircleMore,
  MessageCircleQuestionMark,
  MessageSquareText,
  MonitorSmartphone,
  Moon,
  MoveRight,
  Newspaper,
  NotepadText,
  OctagonAlert,
  Package,
  Palette,
  PanelLeftClose,
  PanelLeftOpen,
  Pause,
  Pencil,
  Percent,
  Play,
  Plus,
  QrCode,
  Receipt,
  RectangleEllipsis,
  RefreshCw,
  Rocket,
  RotateCcwKey,
  Router,
  Save,
  Search,
  SearchX,
  Send,
  Settings,
  Shield,
  ShieldAlert,
  ShieldCheck,
  ShieldOff,
  ShieldUser,
  ShoppingBasket,
  ShoppingCart,
  Shuffle,
  SlidersHorizontal,
  SlidersVertical,
  Smartphone,
  Split,
  Square,
  SquareCheck,
  SquarePlay,
  Star,
  Sun,
  SunMoon,
  Table,
  Tag,
  Tags,
  Trash2,
  TrendingDown,
  TrendingUp,
  Trophy,
  TriangleAlert,
  Unlink,
  Upload,
  User,
  UserPlus,
  UserX,
  Users,
  Vote,
  Wallet,
  Webhook,
  Wrench,
  X,
  Zap
} from 'lucide-vue-next'

const MAP = new Map([
  // People / accounts
  [mdiAccount, User],
  [mdiAccountOutline, User],
  [mdiAccountCircle, CircleUser],
  [mdiAccountDetails, ContactRound],
  [mdiAccountGroup, Users],
  [mdiAccountGroupOutline, Users],
  [mdiAccountMultiple, Users],
  [mdiAccountMultiplePlusOutline, UserPlus],
  [mdiAccountPlusOutline, UserPlus],
  [mdiAccountOffOutline, UserX],
  [mdiShieldAccountOutline, ShieldUser],

  // Alerts / status
  [mdiAlert, TriangleAlert],
  [mdiAlertOutline, TriangleAlert],
  [mdiAlertCircle, CircleAlert],
  [mdiAlertCircleOutline, CircleAlert],
  [mdiAlertOctagonOutline, OctagonAlert],
  [mdiAlertDecagram, BadgeAlert],
  [mdiCheck, Check],
  [mdiCheckCircle, CircleCheck],
  [mdiCheckCircleOutline, CircleCheck],
  [mdiCheckDecagram, BadgeCheck],
  [mdiCheckDecagramOutline, BadgeCheck],
  [mdiInformation, Info],
  [mdiInformationOutline, Info],
  [mdiHelpCircleOutline, CircleQuestionMark],
  [mdiFrequentlyAskedQuestions, MessageCircleQuestionMark],
  [mdiCancel, Ban],
  [mdiClose, X],
  [mdiCloseCircle, CircleX],
  [mdiCloseCircleOutline, CircleX],
  [mdiBellOutline, Bell],
  [mdiBellRingOutline, BellRing],

  // Navigation / chrome
  [mdiMenu, Menu],
  // Mobile aside toggles: back = collapse the panel, forward = expand it.
  [mdiBackburger, PanelLeftClose],
  [mdiForwardburger, PanelLeftOpen],
  [mdiArrowLeft, ArrowLeft],
  [mdiArrowRight, ArrowRight],
  [mdiFormatListBulleted, List],
  [mdiChevronDown, ChevronDown],
  [mdiChevronLeft, ChevronLeft],
  [mdiChevronRight, ChevronRight],
  [mdiChevronUp, ChevronUp],
  [mdiPageFirst, ChevronFirst],
  [mdiPageLast, ChevronLast],
  [mdiDotsVertical, EllipsisVertical],
  [mdiOpenInNew, ExternalLink],
  [mdiLinkVariant, Link],
  // A dead reset link (ResetPassword): the broken chain, in the same diagonal
  // style as `Link` above — Lucide's `Link2Off` is the other chain, struck out.
  [mdiLinkVariantOff, Unlink],
  [mdiHomeOutline, House],
  [mdiViewDashboard, LayoutDashboard],
  [mdiViewGridOutline, LayoutGrid],
  [mdiLoginVariant, LogIn],
  [mdiLogout, LogOut],

  // Actions
  [mdiMagnify, Search],
  [mdiCardSearchOutline, Search],
  [mdiMagnifyClose, SearchX],
  [mdiPencil, Pencil],
  [mdiDelete, Trash2],
  [mdiTrashCan, Trash2],
  [mdiContentCopy, Copy],
  [mdiContentDuplicate, CopyPlus],
  [mdiContentSaveOutline, Save],
  [mdiPlus, Plus],
  // Quick adjust — add or remove votes. MDI's is a "+/−" key; Lucide has no
  // such glyph, and `Diff` (a plus over a minus) says the same thing.
  [mdiPlusMinusVariant, Diff],
  [mdiAutorenew, RefreshCw],
  [mdiRefresh, RefreshCw],
  [mdiReload, RefreshCw],
  [mdiDownload, Download],
  [mdiDownloadOutline, Download],
  [mdiUpload, Upload],
  [mdiSend, Send],
  [mdiShuffleVariant, Shuffle],
  [mdiCog, Settings],
  [mdiCogOutline, Settings],
  [mdiCodeBraces, Braces],
  [mdiCompassOutline, Compass],
  [mdiTrophyVariantOutline, Trophy],
  [mdiTuneVariant, SlidersHorizontal],
  [mdiTuneVertical, SlidersVertical],
  [mdiWrench, Wrench],
  [mdiEye, Eye],
  [mdiEyeOutline, Eye],
  [mdiEyeOff, EyeOff],
  [mdiEyeOffOutline, EyeOff],

  // Media / playback
  [mdiPlay, Play],
  [mdiPlayCircle, CirclePlay],
  [mdiPlayCircleOutline, CirclePlay],
  [mdiPlayBoxOutline, SquarePlay],
  [mdiPause, Pause],
  [mdiPauseCircle, CirclePause],
  [mdiPauseCircleOutline, CirclePause],

  // Forms / selection
  [mdiCheckboxBlankOutline, Square],
  [mdiCheckboxMarked, SquareCheck],
  [mdiFormTextboxPassword, RectangleEllipsis],
  [mdiAsterisk, Asterisk],
  [mdiBallot, ClipboardList],
  [mdiBallotOutline, ClipboardList],
  [mdiVote, Vote],
  [mdiSelectColor, Palette],

  // Lists / sorting
  [mdiFormatListBulletedTriangle, List],
  [mdiFormatListChecks, ListChecks],
  [mdiFormatListNumbered, ListOrdered],
  [mdiSort, ArrowUpDown],
  [mdiSortVariant, ListFilter],
  // "Clear filters" on the logs tables.
  [mdiFilterRemoveOutline, FunnelX],
  [mdiSortAscending, ArrowUpNarrowWide],
  [mdiSortDescending, ArrowDownWideNarrow],

  // Charts / metrics
  [mdiChartBar, ChartColumn],
  [mdiChartBoxOutline, ChartColumn],
  [mdiChartLine, ChartLine],
  [mdiFinance, ChartLine],
  [mdiChartPie, ChartPie],
  [mdiChartTimelineVariant, ChartSpline],
  [mdiTrendingUp, TrendingUp],
  [mdiTrendingDown, TrendingDown],
  [mdiTrendingNeutral, MoveRight],
  [mdiSpeedometer, Gauge],
  [mdiSpeedometerSlow, Gauge],
  [mdiPercentOutline, Percent],
  [mdiAbTesting, FlaskConical],
  [mdiFlaskOutline, FlaskConical],

  // Commerce / money
  [mdiCart, ShoppingCart],
  [mdiCartOutline, ShoppingCart],
  [mdiCartPlus, ShoppingCart],
  [mdiBasketPlusOutline, ShoppingBasket],
  [mdiCashPlus, Wallet],
  [mdiWalletOutline, Wallet],
  [mdiCashMinus, BanknoteArrowDown],
  [mdiCashMultiple, Banknote],
  [mdiCreditCardOutline, CreditCard],
  [mdiReceipt, Receipt],
  [mdiReceiptOutline, Receipt],
  [mdiGift, Gift],
  [mdiGiftOutline, Gift],
  [mdiTagOutline, Tag],
  [mdiTagMultiple, Tags],
  [mdiStar, Star],
  [mdiStarOutline, Star],
  [mdiPackageVariant, Package],
  [mdiPackageVariantClosed, Package],

  // Mail / messaging
  [mdiEmail, Mail],
  [mdiEmailOutline, Mail],
  [mdiMail, Mail],
  [mdiEmailAlert, MailWarning],
  [mdiEmailAlertOutline, MailWarning],
  [mdiEmailCheck, MailCheck],
  [mdiEmailFastOutline, Send],
  [mdiInboxArrowDown, Inbox],
  [mdiChat, MessageCircle],
  [mdiChatProcessingOutline, MessageCircleMore],
  [mdiMessageText, MessageSquareText],

  // Security
  [mdiLock, Lock],
  [mdiLockReset, RotateCcwKey],
  [mdiKeyOutline, KeyRound],
  [mdiShieldOutline, Shield],
  [mdiShieldCheck, ShieldCheck],
  [mdiShieldCheckOutline, ShieldCheck],
  [mdiShieldLock, ShieldCheck],
  // Lucide has no shield-with-a-lock; same stand-in as the filled one above.
  [mdiShieldLockOutline, ShieldCheck],
  [mdiShieldAlertOutline, ShieldAlert],
  [mdiShieldOff, ShieldOff],
  [mdiCellphoneKey, Smartphone],

  // Documents / content
  [mdiFileDocumentOutline, FileText],
  [mdiFileDownloadOutline, FileDown],
  [mdiTextBoxOutline, FileText],
  [mdiTextBoxSearchOutline, FileSearch],
  [mdiFileExportOutline, FileOutput],
  [mdiPostOutline, NotepadText],
  [mdiNewspaperVariantOutline, Newspaper],
  [mdiClipboardCheckOutline, ClipboardCheck],
  [mdiBook, Book],
  [mdiBookOpenPageVariant, BookOpen],
  [mdiSchool, GraduationCap],
  [mdiTimelineTextOutline, History],
  [mdiTranslate, Languages],
  [mdiFeather, Feather],
  [mdiQrcode, QrCode],
  [mdiTableBorder, Table],
  // Empty-state "no table data" glyph; Lucide has no table-off, a struck-out grid is closest.
  [mdiTableOff, Grid2x2X],

  // Time
  [mdiClock, Clock],
  [mdiClockOutline, Clock],
  [mdiCalendarBlankOutline, Calendar],
  [mdiCalendarClock, CalendarClock],

  // Tech / infrastructure
  [mdiApi, Webhook],
  [mdiSourceBranch, GitBranch],
  [mdiCallSplit, Split],
  [mdiRouterNetwork, Router],
  [mdiMonitorCellphone, MonitorSmartphone],
  [mdiCloudOffOutline, CloudOff],
  [mdiBugOutline, Bug],
  [mdiRobotOutline, Bot],
  [mdiRocket, Rocket],
  [mdiRocketLaunch, Rocket],
  [mdiRocketLaunchOutline, Rocket],
  [mdiEarth, Globe],
  [mdiWeb, Globe],
  [mdiLifebuoy, LifeBuoy],
  [mdiHandshakeOutline, Handshake],

  // Theme / misc
  [mdiThemeLightDark, SunMoon],
  [mdiWhiteBalanceSunny, Sun],
  [mdiWeatherNight, Moon],
  [mdiLightbulbOnOutline, Lightbulb],
  [mdiLightningBolt, Zap],
  [mdiFlashOutline, Zap]
])

/**
 * @param {string} path Resolved MDI path data (e.g. the value of `mdiRocket`).
 * @returns {import('vue').Component | null} Matching Lucide component, or null when the
 *   caller should fall back to rendering the raw MDI path.
 */
export const lucideForMdiPath = (path) => MAP.get(path) ?? null

/** Unmapped on purpose (the brand marks — see above), so Icon.vue's dev warning skips them. */
export const intentionallyUnmapped = new Set([mdiDiscord, mdiGithub, mdiGoogle])

export default lucideForMdiPath
